import type { ButtonHTMLAttributes, FormEvent, ReactNode } from "react";
import { AlertCircle, Check, ChevronLeft, ChevronRight, LoaderCircle, X } from "lucide-react";

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

export function LoadingScreen({ error }: { error?: string }) {
  return (
    <div className="loading-screen" role="status">
      <div className="brand-symbol" aria-hidden="true"><span>A</span></div>
      {error ? <><AlertCircle size={22} /><strong>{error}</strong></> : <LoaderCircle className="spin" size={24} />}
    </div>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  compact?: boolean;
};

export function Button({ icon, variant = "secondary", compact, className, children, ...props }: ButtonProps) {
  return (
    <button className={cx("button", `button--${variant}`, compact && "button--compact", className)} {...props}>
      {icon ? <span className="button__icon" aria-hidden="true">{icon}</span> : null}
      {children ? <span>{children}</span> : null}
    </button>
  );
}

export function IconButton({ label, children, className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button className={cx("icon-button", className)} aria-label={label} title={label} {...props}>
      {children}
    </button>
  );
}

export function PageHeader({ title, count, subtitle, actions }: { title: string; count?: number; subtitle?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div className="page-header__title">
        <h1>{title}{typeof count === "number" ? <small> ({count})</small> : null}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <nav className="page-actions" aria-label="Akcje widoku">{actions}</nav> : null}
    </header>
  );
}

export function FilterSidebar({ children, title = "Filtry", open = true, onToggle }: { children: ReactNode; title?: string; open?: boolean; onToggle?: () => void }) {
  return (
    <aside className={cx("filter-sidebar", !open && "filter-sidebar--collapsed")}>
      <div className="filter-sidebar__rail">
        <IconButton label={open ? "Zwiń filtry" : "Rozwiń filtry"} onClick={onToggle}>
          {open ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
        </IconButton>
      </div>
      <div className="filter-sidebar__content" aria-hidden={!open}>
        <h2>{title}</h2>
        {children}
      </div>
    </aside>
  );
}

export function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return <section className="filter-group"><h3>{title}</h3>{children}</section>;
}

export function Field({ label, children, hint, className }: { label: string; children: ReactNode; hint?: string; className?: string }) {
  return (
    <label className={cx("field", className)}>
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="check-row">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="check-box" aria-hidden="true">{checked ? <Check size={13} /> : null}</span>
      <span>{label}</span>
    </label>
  );
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: Array<{ value: T; label: string; count?: number }>; onChange: (value: T) => void }) {
  return (
    <div className="segmented" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className={cx(value === option.value && "is-active")}
          onClick={() => onChange(option.value)}
        >
          {option.label}{typeof option.count === "number" ? <span>{option.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Badge({ status, children }: { status?: string; children: ReactNode }) {
  return <span className={cx("status-badge", status && `status-badge--${status}`)}>{children}</span>;
}

export function Avatar({ initials, colorIndex = 0, size = "medium" }: { initials: string; colorIndex?: number; size?: "small" | "medium" | "large" }) {
  return <span className={cx("avatar", `avatar--${size}`, `avatar--color-${Math.abs(colorIndex) % 6}`)}>{initials}</span>;
}

export function Modal({ title, children, footer, onClose, width = "560px" }: { title: string; children: ReactNode; footer?: ReactNode; onClose: () => void; width?: string }) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title" style={{ maxWidth: width }}>
        <header className="modal__header">
          <h2 id="modal-title">{title}</h2>
          <IconButton label="Zamknij" onClick={onClose}><X size={18} /></IconButton>
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </section>
    </div>
  );
}

export function FormModal({ title, children, onClose, onSubmit, submitLabel = "Zapisz", busy }: { title: string; children: ReactNode; onClose: () => void; onSubmit: (event: FormEvent<HTMLFormElement>) => void; submitLabel?: string; busy?: boolean }) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={<><Button type="button" onClick={onClose}>Anuluj</Button><Button type="submit" form="modal-form" variant="primary" disabled={busy}>{busy ? "Zapisywanie..." : submitLabel}</Button></>}
    >
      <form id="modal-form" className="form-grid" onSubmit={onSubmit}>{children}</form>
    </Modal>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <div className="empty-state"><strong>{title}</strong>{description ? <p>{description}</p> : null}{action}</div>;
}

export function Toast({ message, tone = "success" }: { message: string; tone?: "success" | "error" }) {
  return <div className={cx("toast", `toast--${tone}`)} role="status">{tone === "success" ? <Check size={17} /> : <AlertCircle size={17} />}{message}</div>;
}

export function formatMinutes(value: number | undefined | null): string {
  const total = Number(value || 0);
  // Display rounds to the nearest whole second, symmetrically away from zero
  // at half-seconds. Stored/report numeric values retain millisecond precision.
  const seconds = Number.isFinite(total) ? Math.round(Math.abs(total) * 60) : 0;
  const sign = total < 0 && seconds !== 0 ? "-" : "";
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor(seconds % 3600 / 60);
  const remainder = seconds % 60;
  return `${sign}${hours}h ${String(mins).padStart(2, "0")}min${remainder ? ` ${String(remainder).padStart(2, "0")}s` : ""}`;
}

export function formatDate(value: string | undefined | null): string {
  if (!value) return "-";
  return new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(value.length === 10 ? `${value}T12:00:00` : value));
}

export function formatTime(value: string | undefined | null): string {
  if (!value) return "-";
  return new Intl.DateTimeFormat("pl-PL", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}
